import {
	canUserRequestAccess,
	getUsersFromCollaborationGraph,
	getCollaboratorsWithJiraAccess,
	isSite7DaysOld,
	hasAccessToProduct,
	isSiteOptedOut,
	isUserTooNew,
} from '@atlassian/cross-join-nudges';
import type { Collaborators } from '@atlassian/cross-join-nudges';

import { withEligibilityCache } from './caching.ts';
import { getFeatureVisitedCount, isUserOptedOut } from './feature-visited.ts';

export type IneligibilityReason =
	| 'SITE_OPTED_OUT'
	| 'NEW_SITE_LESS_THAN_7_DAYS_OLD'
	| 'ALREADY_HAVE_ACCESS'
	| 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS'
	| 'NO_COLLABORATORS_FOUND'
	| 'COLLABORATORS_HAVE_NO_ACCESS'
	| 'USER_IS_TOO_NEW'
	| 'FEATURE_VISITED_COUNT_MAXED_OUT'
	| 'USER_HAS_OPTED_OUT'
	| 'MSR_NUDGE_NOT_RECOMMENDED';

export type ResolverInput = {
	userId: string;
	cloudId: string;
	getMSRNudgeRecommendations: () => Promise<string[]>;
};

export type ResolverResult =
	| {
			ineligibilityReason: IneligibilityReason;
	  }
	| {
			ineligibilityReason: null;
			collaborators: Collaborators;
	  };

const MAX_FEATURE_VISIT_COUNT = 3;
export const CONF_TO_JIRA_INTEGRATION = 'crossJoinNudgesV3';

export const resolveCrossJoinNudges = async ({
	userId,
	cloudId,
	getMSRNudgeRecommendations,
}: ResolverInput): Promise<ResolverResult> => {
	const cachedUserIsTooNew = withEligibilityCache('user_is_too_new', isUserTooNew, 1);

	if (getFeatureVisitedCount(CONF_TO_JIRA_INTEGRATION) >= MAX_FEATURE_VISIT_COUNT) {
		return {
			ineligibilityReason: 'FEATURE_VISITED_COUNT_MAXED_OUT',
		};
	}

	if (isUserOptedOut(CONF_TO_JIRA_INTEGRATION)) {
		return {
			ineligibilityReason: 'USER_HAS_OPTED_OUT',
		};
	}

	if (await isSiteOptedOut(cloudId)) {
		return {
			ineligibilityReason: 'SITE_OPTED_OUT',
		};
	}

	if (await hasAccessToProduct('jira-software', cloudId)) {
		return {
			ineligibilityReason: 'ALREADY_HAVE_ACCESS',
		};
	}

	if (await cachedUserIsTooNew(cloudId, userId, 'confluence_first_active')) {
		return {
			ineligibilityReason: 'USER_IS_TOO_NEW',
		};
	}

	const collaborators = await getUsersFromCollaborationGraph('crossJoinNudgesV3', cloudId, userId);
	if (collaborators === undefined || collaborators.length === 0) {
		return {
			ineligibilityReason: 'NO_COLLABORATORS_FOUND',
		};
	}

	const collaboratorsWithJiraAccess = await getCollaboratorsWithJiraAccess(cloudId, collaborators);
	if (collaboratorsWithJiraAccess.length === 0) {
		return {
			ineligibilityReason: 'COLLABORATORS_HAVE_NO_ACCESS',
		};
	}

	if (!(await canUserRequestAccess('jira-software', cloudId))) {
		return {
			ineligibilityReason: 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS',
		};
	}

	if (!(await isSite7DaysOld('jira-software', cloudId))) {
		return {
			ineligibilityReason: 'NEW_SITE_LESS_THAN_7_DAYS_OLD',
		};
	}

	const recommendations = await getMSRNudgeRecommendations();

	if (!recommendations.includes('cjn-conf-jira-v3')) {
		return {
			ineligibilityReason: 'MSR_NUDGE_NOT_RECOMMENDED',
		};
	}

	return {
		ineligibilityReason: null,
		collaborators: collaboratorsWithJiraAccess,
	};
};
